import { Injectable, signal } from '@angular/core';

// ─── Public config type (what callers pass in) ────────────────────────────────
export interface ConfirmConfig {
  title:         string;
  message:       string;
  confirmLabel?: string;  // default: 'Confirm'
  cancelLabel?:  string;  // default: 'Cancel'
  variant?:      'danger' | 'default';
}

// ─── Internal type (adds the promise resolver) ────────────────────────────────
export interface ActiveModal extends Required<ConfirmConfig> {
  resolve: (answer: boolean) => void;
}

/**
 * ModalService
 *
 * Injectable service for showing confirmation modals from anywhere in the app.
 *
 * Usage:
 *   const confirmed = await this.modalService.confirm({
 *     title: 'Log out',
 *     message: 'Are you sure you want to end your session?',
 *     confirmLabel: 'Log out',
 *     variant: 'danger',
 *   });
 *   if (confirmed) { ... }
 */
@Injectable({ providedIn: 'root' })
export class ModalService {

  /** Currently active modal, or null when none is open. */
  private readonly _active = signal<ActiveModal | null>(null);

  /** Read-only signal consumed by ModalOutlet. */
  readonly active = this._active.asReadonly();

  /**
   * Open a confirmation dialog.
   * Resolves with `true` if the user clicks confirm, `false` on cancel/backdrop.
   */
  confirm(config: ConfirmConfig): Promise<boolean> {
    return new Promise((resolve) => {
      this._active.set({
        title:         config.title,
        message:       config.message,
        confirmLabel:  config.confirmLabel ?? 'Confirm',
        cancelLabel:   config.cancelLabel  ?? 'Cancel',
        variant:       config.variant      ?? 'default',
        resolve,
      });
    });
  }

  /** Called by ModalOutlet when the user makes a choice. */
  respond(answer: boolean): void {
    const modal = this._active();
    if (modal) {
      modal.resolve(answer);
      this._active.set(null);
    }
  }
}
